import type { SupabaseClient } from '@supabase/supabase-js';

import {
  filterConnectionsForBrandKit,
  resolveWorkspaceConnectionsForPlatform,
} from './resolveBrandVisibleConnections';

export type ActivePlatformConnection = {
  id: string;
  account_type: string | null;
  platform_user_id: string | null;
};

export type ActiveConnectionLookup =
  | { status: 'connected'; connection: ActivePlatformConnection }
  | { status: 'not_connected' }
  | { status: 'needs_reconnect' }
  | { status: 'ambiguous'; count: number }
  | { status: 'invalid_override' }
  | { status: 'error'; message: string };

/**
 * The ONLY last_error value this guard blocks on.
 *
 * `refresh_connection_token_health()` (hourly cron) writes exactly this string
 * when a connection's vault secret has vanished, and — load-bearing — clears it
 * back to NULL the moment the secret exists again. That self-healing round trip
 * is why blocking on it can't strand a user.
 *
 * Deliberately NOT the broader `isReconnectError()` predicate the UI uses
 * (`includes('token')`, `includes('expired')`, …): the cron only clears the
 * EXACT string, so blocking on a fuzzy match would let any other token-ish
 * message become a permanent, uncleanable publish block.
 */
const VAULT_SECRET_MISSING = 'token_invalid';

/**
 * NOTE: this is a FLAT interface, not a discriminated union. The repo compiles
 * with `strictNullChecks: false`, under which TypeScript does not narrow a union
 * by a boolean discriminant (`if (!guard.connected)` would not expose the
 * error-branch fields). A flat shape sidesteps that: every field is always
 * present, so callers read them without narrowing. `connection` is only
 * meaningful when `connected` is true; `reason: 'connected'` marks that case.
 */
export interface ConnectionGuardOutcome {
  connected: boolean;
  connection: ActivePlatformConnection | null;
  reason:
    'connected' | 'not_connected' | 'needs_reconnect' | 'ambiguous' | 'invalid_override' | 'error';
  message: string;
  retryable: boolean;
  missingPlatform: string | null;
}

/**
 * Resolve the single active connection for (ownerId, platform).
 *
 * `user_connections` legitimately holds MULTIPLE rows per (user_id, platform):
 * the partial unique index `user_connections_active_native_account_unique` only
 * forbids two ACTIVE rows sharing platform_user_id, so a stale is_active=false
 * row can coexist with the live one. A guard that fetched by (user_id, platform)
 * alone and called `.maybeSingle()` therefore errored on that extra row and —
 * because the error was discarded — mis-reported the platform as "not connected"
 * (BUG-INTEGRATIONS-PUBLISH-001; team members hit it because they read the
 * account owner's connections, and owner accounts accrue reconnect history).
 *
 * Mirrors the edge function's selectUnambiguousConnections contract on the
 * client: filter is_active, then 0 -> not_connected, exactly 1 -> connected,
 * 2+ active accounts -> ambiguous. Errors are returned, never swallowed.
 *
 *   .eq('is_active', true)  <-- load-bearing: drops the stale row, collapsing
 *                               the active+inactive case to exactly one row.
 *
 * `is_active` alone does NOT mean publishable. A revoked/expired token leaves the
 * row active while its vault secret is gone; the health cron records that as
 * last_error='token_invalid'. The UI already treats that as amber "Reconnect"
 * (deriveConnectionStatus), but this guard used to still answer "connected", so
 * Post Now sailed through and failed later at the platform API — the same
 * UI-vs-guard disagreement as the original bug, just inverted. Prod carries 3
 * ACTIVE rows in exactly that state today.
 */
export async function getActivePlatformConnection(
  supabase: SupabaseClient,
  ownerId: string,
  platform: string,
  /**
   * Per-platform destination override, straight from `social_posts
   * .destination_connection_ids[platform]` (see that column's comment for the
   * full contract). `undefined` (key missing) and `null` (explicit JSON null)
   * both mean "no override" — fall through to the normal ambiguity gate.
   *
   * A present, non-null id is a hard requirement: if it does not match one of
   * THIS platform's active rows (stale/archived/foreign), we must NOT
   * silently re-resolve via the ambiguity gate — that could publish as a
   * different account than the post says, which is public and irreversible.
   * Only a match short-circuits straight to 'connected', bypassing the
   * ambiguous-count check entirely (an explicit, valid choice is never
   * ambiguous, even with 2+ active rows for the platform).
   */
  explicitConnectionId?: string | null,
  /**
   * The workspace this publish belongs to (`brand_kits.id`). Omitted or null
   * means "no workspace filter" — every existing caller, and therefore
   * byte-identical behaviour to before this parameter existed.
   *
   * When supplied, `user_connections.brand_kit_id` decides which rows count.
   * NULL on a row means account-wide (see resolveBrandVisibleConnections).
   * All 45 production rows are NULL as of 2026-08-22, so passing a workspace
   * changes nothing until connections are actually assigned — deliberately.
   */
  brandKitId?: string | null,
): Promise<ActiveConnectionLookup> {
  const { data, error } = await supabase
    .from('user_connections')
    .select('id, account_type, platform_user_id, last_error, brand_kit_id')
    .eq('user_id', ownerId)
    .eq('platform', platform)
    .eq('is_active', true);

  if (error) return { status: 'error', message: error.message };

  const allRows = (data ?? []) as (ActivePlatformConnection & {
    last_error?: string | null;
    brand_kit_id: string | null;
  })[];

  const scope = brandKitId ?? null;

  // Two different questions, two different filters.
  //
  // An EXPLICIT choice is honoured whether the connection is this workspace's
  // own or account-wide, so it uses plain VISIBILITY — applying precedence
  // here would reject a deliberate, valid pick of an account-wide account
  // merely because the workspace also owns one. It still cannot reach another
  // workspace's connection: that falls out as `invalid_override`, which fails
  // loud rather than silently re-resolving to a different account.
  //
  // The AMBIGUITY path uses PRECEDENCE — this workspace's own connection wins
  // outright over a legacy account-wide one, so scoping can never turn a
  // previously-publishable platform into `ambiguous`.
  if (explicitConnectionId != null) {
    const visible = filterConnectionsForBrandKit(allRows, scope);
    const row = visible.find((r) => r.id === explicitConnectionId);
    if (!row) return { status: 'invalid_override' };
    if (row.last_error === VAULT_SECRET_MISSING) return { status: 'needs_reconnect' };
    return {
      status: 'connected',
      connection: {
        id: row.id,
        account_type: row.account_type,
        platform_user_id: row.platform_user_id,
      },
    };
  }

  const rows = resolveWorkspaceConnectionsForPlatform(allRows, scope);

  if (rows.length === 0) return { status: 'not_connected' };
  if (rows.length > 1) return { status: 'ambiguous', count: rows.length };

  const [row] = rows;
  if (row.last_error === VAULT_SECRET_MISSING) return { status: 'needs_reconnect' };

  return {
    status: 'connected',
    connection: {
      id: row.id,
      account_type: row.account_type,
      platform_user_id: row.platform_user_id,
    },
  };
}

/**
 * Pure mapper: lookup -> normalized guard outcome. No I/O; all guards
 * (PublishService, publish_now, schedule_content) translate this ONE outcome to
 * their own error shape, so the not-connected/ambiguous/error copy lives here once.
 */
export function connectionGuardOutcome(
  lookup: ActiveConnectionLookup,
  platform: string,
): ConnectionGuardOutcome {
  switch (lookup.status) {
    case 'connected':
      return {
        connected: true,
        connection: lookup.connection,
        reason: 'connected',
        message: '',
        retryable: false,
        missingPlatform: null,
      };
    case 'not_connected':
      return {
        connected: false,
        connection: null,
        reason: 'not_connected',
        message: `${platform} is not connected. Connect it in Settings and try again.`,
        retryable: true,
        missingPlatform: platform,
      };
    // Connected but unusable: the stored token is gone. Distinct copy from
    // "not connected" — the account IS linked, so telling someone to "connect
    // it" sends them looking for a tile that isn't in the Connect list.
    case 'needs_reconnect':
      return {
        connected: false,
        connection: null,
        reason: 'needs_reconnect',
        message: `${platform} lost its connection and needs reconnecting. Reconnect it in Settings and try again.`,
        retryable: true,
        missingPlatform: null,
      };
    case 'ambiguous':
      return {
        connected: false,
        connection: null,
        reason: 'ambiguous',
        message: `Multiple active ${platform} accounts are connected. Choose a specific account before publishing.`,
        retryable: false,
        missingPlatform: null,
      };
    // The post recorded a specific destination connection for this platform,
    // and that connection is no longer in the active set (stale, archived, or
    // reassigned). Distinct from `ambiguous` and from `not_connected`: the
    // platform itself may well have exactly one (different) active account —
    // silently falling back to it would publish as the wrong account.
    case 'invalid_override':
      return {
        connected: false,
        connection: null,
        reason: 'invalid_override',
        message: `The ${platform} account this post was set to publish as is no longer available. Choose a connection and try again.`,
        retryable: false,
        missingPlatform: null,
      };
    case 'error':
      return {
        connected: false,
        connection: null,
        reason: 'error',
        message: `Could not verify ${platform} connection: ${lookup.message}`,
        retryable: true,
        missingPlatform: null,
      };
  }
}

/**
 * Convenience: query + map in one call. Guards call this and translate the
 * single ConnectionGuardOutcome to their own error shape.
 */
export async function resolveConnectionGuard(
  supabase: SupabaseClient,
  ownerId: string,
  platform: string,
  explicitConnectionId?: string | null,
): Promise<ConnectionGuardOutcome> {
  return connectionGuardOutcome(
    await getActivePlatformConnection(supabase, ownerId, platform, explicitConnectionId),
    platform,
  );
}

/** The error shape the agent tools (publish_now, schedule_content) return. */
export type GuardToolError = {
  state: 'error';
  error: string;
  retryable: boolean;
  missing_platform?: string;
};

/**
 * Adapt a guard outcome to the agent tools' error shape, or null when connected
 * (meaning: proceed). Shared by both publish_now and schedule_content so the
 * outcome->tool-error translation lives in exactly one place.
 */
export function guardOutcomeToToolError(guard: ConnectionGuardOutcome): GuardToolError | null {
  if (guard.connected) return null;
  return {
    state: 'error',
    error: guard.message,
    retryable: guard.retryable,
    ...(guard.missingPlatform ? { missing_platform: guard.missingPlatform } : {}),
  };
}

/** Resolve + adapt in one call for the agent tools. */
export async function resolveConnectionToolError(
  supabase: SupabaseClient,
  ownerId: string,
  platform: string,
  explicitConnectionId?: string | null,
): Promise<GuardToolError | null> {
  return guardOutcomeToToolError(
    await resolveConnectionGuard(supabase, ownerId, platform, explicitConnectionId),
  );
}
